import type { MerchantPortalNavFeatureKey } from './merchantPortalNavVisibility';
import { grantsAnythingUnder, satisfiesPermission } from '@/shared/permissions';
import type { PermissionCode, PermissionRequirement } from '@/shared/permissions';

/**
 * The registry of everything a merchant user can navigate to, and what it costs to get in.
 *
 * <p><b>Why this exists.</b> Until now the portal answered "may I see this?" in three unrelated
 * places: the sidebar filtered its own hard-coded {@code permission} strings, each page re-derived
 * its own {@code canView} through a feature hook, and the route table gated almost nothing. The three
 * drifted, exactly as duplicated authorization always does — the loyalty sidebar entry pointed at a
 * screen that answered "Access restricted", several pages read {@code canView} and then rendered the
 * screen anyway, and typing a URL walked straight past the sidebar into a section the user had no
 * right to. A staff member with only warehouse access could read the whole catalogue by guessing
 * {@code /catalog/products}.
 *
 * <p><b>What it fixes.</b> One entry per navigable area, naming its path, its label and the permission
 * that opens it. The sidebar renders from it, the router guards from it, and the landing page picks a
 * destination from it — so a section that is hidden is also unreachable, by construction rather than by
 * remembering to write the check twice.
 *
 * <p><b>This is not the security boundary.</b> The BFF enforces the same permission on every call; a
 * hand-crafted request to a forbidden endpoint is refused server-side whatever the SPA renders. What
 * this registry buys is a portal that shows a user their job and nothing else — and that never
 * advertises a door it will not open.
 */

/** Stable identifier for a navigable area. Used by the sidebar, the route guards and the tests. */
export type AppSectionKey =
  | 'dashboard'
  | 'catalogProducts'
  | 'catalogCategories'
  | 'catalogStructure'
  | 'catalogPricing'
  | 'catalogPublication'
  | 'channelStock'
  | 'catalogImport'
  | 'promo'
  | 'content'
  | 'customers'
  | 'messaging'
  | 'conversations'
  | 'chatbot'
  | 'promoCalendar'
  | 'loyalty'
  | 'loyaltyProgram'
  | 'inventory'
  | 'storeLocations'
  | 'legacyProducts'
  | 'orders'
  | 'media'
  | 'team'
  | 'billing'
  | 'audit'
  | 'settings';

/** What the permission check needs to answer a section's "may I enter?" question. */
export interface SectionPermissionContext {
  /** Every code granted to the session, wildcards included. */
  permissions: PermissionCode[];
  /** Wildcard-aware single-code check. */
  hasPermission: (permission: PermissionCode) => boolean;
}

export interface AppSection {
  key: AppSectionKey;
  /** Where the sidebar links, and where a redirect sends someone who lands here. */
  path: string;
  /** `common.json` key for the human-readable name. Used by the nav and the "go here instead" links. */
  labelKey: string;
  /**
   * The permission that opens the section. An array means **any one** of the codes suffices
   * (see `PermissionRequirement`). Omitted only for areas open to every authenticated user.
   */
  permission?: PermissionRequirement;
  /**
   * Availability that a code list cannot express — currently only Settings, which opens when the user
   * can view *any* of its ~30 domains. Takes precedence over {@link permission} when both are set.
   */
  isAvailable?: (context: SectionPermissionContext) => boolean;
  /** Sidebar feature flag. A section switched off here is hidden **and** refused, like a missing permission. */
  featureKey?: MerchantPortalNavFeatureKey;
  /**
   * True when the area is reachable but deliberately absent from the sidebar — a detail screen, a
   * legacy path kept for bookmarks, or a workspace entered from somewhere else.
   */
  navHidden?: boolean;
}

/**
 * The dashboard is a **commerce and finance** summary — revenue, expenses, cash balance, net profit,
 * active customers. It opens for anyone who may read one of those, and for nobody else.
 *
 * <p><b>`catalog.view` was here and has been removed.</b> It let a warehouse or catalogue-only employee
 * land on a revenue summary: the merchant who reported this expected their «Склад» user to see Товары,
 * Категории, Шаблоны, Склад and Места хранения — and nothing else. Only one of the dashboard's six tiles
 * is catalogue data (a product count), which is not worth the other five.
 *
 * <p>This is still a heuristic standing in for a permission the platform does not mint. The figures are
 * `MOCK_DASHBOARD` behind a visible notice today; when `/` shows live revenue it needs its own code from
 * `RolePermissionMapper` and the access catalogue, not a union of three unrelated ones.
 */
const DASHBOARD_PERMISSIONS: PermissionCode[] = [
  'settings.orders.view',
  'customers.view',
  'catalog.pricing.view',
];

/**
 * Settings namespaces. Nearly every settings domain is `settings.<domain>.view`, but two predate that
 * convention: catalog languages reuses the PIM structure code, and the Evotor process-sync integration
 * mints its own `integrations.evotor.*` family. `settingsNav.test.ts` asserts this list still covers
 * every domain in the rail, so a future domain on a new namespace fails a test instead of silently
 * making Settings unreachable for the people who hold only that code.
 */
export const SETTINGS_NAMESPACE_PREFIXES = ['settings.', 'integrations.evotor.'] as const;
// `catalog.structure.view` predates the convention (the `languages` domain); `content.view` is the
// same shape for a newer domain — the chatbot templates & FAQ editor (plan-conversation-001
// WP-MP.2) is `content.*` work (adr-auth-006 D5), not `settings.chatbot.*`, so an employee holding
// only that code must still be able to open the Settings hub that contains their one domain.
const SETTINGS_EXTRA_CODES: PermissionCode[] = ['catalog.structure.view', 'content.view'];

/**
 * Codes inside those namespaces that open no settings page of their own, and so must not open the hub.
 *
 * <p>`settings.general.*` has no screen at all — it gates the audit trail, which is its own section.
 * `integrations.evotor.*` gates the Automation *tab* inside the Retail page, which itself needs
 * `settings.retail-evotor.view` — so the code alone reaches nothing. `settings.ai-agents.*` joined
 * this list in plan-merchant-portal-019 WP2: the standalone `/settings/ai-agents` page retired into
 * the AI provider card on `/settings/chatbot` (`#ai`) — the credentials tier code is still consulted
 * there, but the domain owns no rail page of its own any more. Counting any of these would seat
 * someone in front of an empty hub, told they may enter a place with nothing in it.
 *
 * <p>Kept honest from the other side: `settingsNav.test.ts` asserts this list names exactly the
 * namespace codes with no nav item, so adding a page for one of them — or adding a page-less code —
 * fails the build rather than drifting.
 */
export const SETTINGS_CODES_WITHOUT_PAGES: readonly PermissionCode[] = [
  'settings.general.view',
  'settings.general.manage',
  'integrations.evotor.view',
  'integrations.evotor.manage',
  'settings.ai-agents.view',
  'settings.ai-agents.manage',
  'settings.ai-agents.credentials.manage',
];

function canReachSettings(context: SectionPermissionContext): boolean {
  // Filtered before the namespace test rather than after, so a genuine `settings.*` wildcard — which
  // does grant every real domain — still opens the hub.
  const opensAPage = context.permissions.filter(
    (code) => !SETTINGS_CODES_WITHOUT_PAGES.includes(code),
  );
  return (
    SETTINGS_NAMESPACE_PREFIXES.some((prefix) => grantsAnythingUnder(opensAPage, prefix)) ||
    SETTINGS_EXTRA_CODES.some(context.hasPermission)
  );
}

/**
 * Every navigable area, in the order the sidebar presents them.
 *
 * <p>The order is also the fallback order: someone who cannot use the dashboard is sent to the first
 * entry here they *can* use, so a warehouse-only account lands on the stock workspace rather than on a
 * refusal.
 */
export const APP_SECTIONS: AppSection[] = [
  {
    key: 'dashboard',
    path: '/',
    labelKey: 'nav.dashboard',
    permission: DASHBOARD_PERMISSIONS,
  },
  {
    key: 'catalogProducts',
    path: '/catalog/products',
    labelKey: 'nav.catalog.products',
    permission: 'catalog.view',
  },
  {
    key: 'catalogCategories',
    path: '/catalog/categories',
    labelKey: 'nav.catalog.categories',
    permission: 'catalog.view',
  },
  {
    key: 'catalogStructure',
    path: '/catalog/templates',
    labelKey: 'nav.catalog.families',
    permission: 'catalog.structure.view',
  },
  {
    // Bulk import reads run history through the catalogue read tier; its write actions gate on
    // `catalog.manage` inside the page (BFF: CatalogImportControllerImpl — "reads require catalog.view,
    // writes catalog.manage"). Entered from the products workspace, so it carries no sidebar entry.
    key: 'catalogImport',
    path: '/catalog/import',
    labelKey: 'nav.catalog.products',
    permission: 'catalog.view',
    navHidden: true,
  },
  {
    key: 'catalogPricing',
    path: '/catalog/pricing',
    labelKey: 'nav.commerce.pricing',
    permission: 'catalog.pricing.view',
    featureKey: 'pricing',
  },
  {
    key: 'promo',
    path: '/promo',
    labelKey: 'nav.commerce.promo',
    permission: 'catalog.promo.view',
    featureKey: 'promo',
  },
  {
    key: 'catalogPublication',
    path: '/catalog/publication',
    labelKey: 'nav.salesChannels.publication',
    permission: 'catalog.publication.view',
    featureKey: 'publication',
  },
  {
    key: 'content',
    path: '/content/pages',
    labelKey: 'nav.salesChannels.content',
    permission: 'content.view',
  },
  {
    key: 'customers',
    path: '/customers',
    labelKey: 'nav.customers.customers',
    permission: 'customers.view',
    featureKey: 'customers',
  },
  {
    // `/loyalty/customers` renders the customer workspace with loyalty preselected: the list itself is a
    // customer search gated on `customers.view`, and loyalty is a tab inside a selected customer that
    // gates separately on `loyalty.view`. Advertising this on `loyalty.view` alone linked to a screen
    // that answered "Access restricted" — so the entry asks for what the screen actually asks for.
    key: 'loyalty',
    path: '/loyalty/customers',
    labelKey: 'nav.customers.loyalty',
    permission: 'customers.view',
    featureKey: 'loyalty',
  },
  {
    // Programme administration — model, tiers, rules, card pool. Genuinely loyalty's own, unlike the
    // customer-facing workspace above. Reached from the loyalty workspace, not the sidebar.
    key: 'loyaltyProgram',
    path: '/loyalty/program',
    labelKey: 'nav.customers.loyalty',
    permission: 'loyalty.view',
    featureKey: 'loyalty',
    navHidden: true,
  },
  {
    // The customer-messaging settings domain, surfaced under Customers because that is where the
    // people who send campaigns look for it — the same screen the Settings rail also lists.
    key: 'messaging',
    path: '/settings/notifications',
    labelKey: 'nav.customers.messaging',
    permission: 'settings.notifications.view',
  },
  {
    // plan-conversation-001 WP-MP.0 registered this ahead of its screen; plan-merchant-portal-019 WP3
    // (= WP-MP.5) shipped the route (`features/conversations/routes.tsx`, `<RequireSection
    // section="conversations">`) and the sidebar entry (`AppLayout.NAV_SECTIONS`). The omnichannel
    // conversation inbox — the merchant's channel-agnostic reply surface for Telegram/MAX and any
    // future channel, built on the customer-conversation hub. `merchantPortal.nav.conversations`
    // still defaults to off (pilot staging, adr-merchant-portal-017): the URL stays reachable for any
    // holder of `conversations.view` — a right is a right; a rollout schedule is not.
    key: 'conversations',
    path: '/conversations',
    labelKey: 'nav.customers.conversations',
    permission: 'conversations.view',
    featureKey: 'conversations',
  },
  {
    // plan-conversation-001 WP-MP.0/plan-merchant-portal-019 WP2: the same "shortcut into Settings"
    // pattern as `messaging` above — the chatbot behaviour screen (General → per channel) has its
    // route, guard and `SETTINGS_NAV`/`SETTINGS_GROUPS` rail card now.
    key: 'chatbot',
    path: '/settings/chatbot',
    labelKey: 'nav.customers.chatbot',
    permission: 'settings.chatbot.view',
  },
  {
    // The promo calendar, surfaced a second time under Customers as the campaign-planning entry.
    // Same workspace, same permission as the Commerce entry above.
    key: 'promoCalendar',
    path: '/promo/calendar',
    labelKey: 'nav.customers.eventCalendar',
    permission: 'catalog.promo.view',
    featureKey: 'promo',
  },
  {
    key: 'inventory',
    path: '/inventory',
    labelKey: 'nav.logistics.inventory',
    permission: 'inventory.view',
    featureKey: 'inventory',
  },
  {
    // plan-platform-001 WP2.3 (design-stock-channels-page §5): channel-availability configuration —
    // which locations feed each sales channel and retail store (groups/scopes/overrides).
    key: 'channelStock',
    path: '/channels/stock',
    labelKey: 'nav.salesChannels.stock',
    permission: 'inventory.view',
    featureKey: 'channelStock',
  },
  {
    // The workspace itself. `/settings/store` still redirects here for old links, but nothing internal
    // should route through that hop: it renders the settings shell for a moment before bouncing, and
    // it makes the sidebar highlight a path the user is never actually on.
    key: 'storeLocations',
    path: '/store',
    labelKey: 'nav.logistics.locations',
    permission: 'settings.store.view',
    featureKey: 'store',
  },
  {
    key: 'legacyProducts',
    path: '/products',
    labelKey: 'nav.catalog.products',
    permission: 'settings.catalog.view',
    navHidden: true,
  },
  {
    key: 'orders',
    path: '/orders',
    labelKey: 'nav.store.orders',
    permission: 'settings.orders.view',
    featureKey: 'orders',
    navHidden: true,
  },
  {
    key: 'media',
    path: '/media',
    labelKey: 'nav.store.media',
    permission: 'settings.media.view',
    featureKey: 'media',
    navHidden: true,
  },
  {
    key: 'team',
    path: '/team',
    labelKey: 'nav.administration.users',
    // Either staff code opens the workspace: `employees.view` lands on the read-only directory,
    // `employees.manage` on the administration tabs. Spelled out because a principal resolved through
    // the BFF's legacy role mapping holds only the manage code.
    permission: ['employees.view', 'employees.manage'],
    featureKey: 'team',
  },
  {
    key: 'billing',
    path: '/billing',
    labelKey: 'nav.administration.billing',
    permission: 'settings.payments.view',
  },
  {
    key: 'audit',
    path: '/audit',
    labelKey: 'nav.administration.audit',
    permission: 'settings.general.view',
  },
  {
    key: 'settings',
    path: '/settings',
    labelKey: 'nav.administration.settings',
    isAvailable: canReachSettings,
  },
];

/** Lookup by key. Every {@link AppSectionKey} resolves — the type and the table are kept in step by a test. */
export const APP_SECTION_BY_KEY: Record<AppSectionKey, AppSection> = Object.fromEntries(
  APP_SECTIONS.map((section) => [section.key, section]),
) as Record<AppSectionKey, AppSection>;

/** The section a key names. Throws rather than silently rendering an ungated screen. */
export function appSection(key: AppSectionKey): AppSection {
  const section = APP_SECTION_BY_KEY[key];
  if (!section) {
    throw new Error(`Unknown app section "${key}" — add it to APP_SECTIONS`);
  }
  return section;
}

/** Sidebar feature-flag state, keyed as `useMerchantPortalNavVisibility()` returns it. */
export type NavVisibility = Record<MerchantPortalNavFeatureKey, boolean>;

/**
 * Whether the session's **rights** open a section. This is what a route guard asks.
 *
 * <p><b>Feature flags deliberately do not count here.</b> They are menu toggles for shipped
 * workspaces staged off per tenant — Loyalty, Orders and Media are "shipped, not staged" today. Making
 * one close a URL would answer a deep link with "Access restricted", which for a user who holds the
 * permission is simply untrue, and would break the pilot links that staging exists to support. A right
 * is a right; a rollout schedule is not.
 */
export function isSectionPermitted(
  section: AppSection,
  context: SectionPermissionContext,
): boolean {
  if (section.isAvailable) {
    return section.isAvailable(context);
  }
  if (!section.permission) {
    return true;
  }
  return satisfiesPermission(section.permission, context.hasPermission);
}

/**
 * Whether a section belongs in the sidebar: the session's rights **and** the tenant's staging.
 *
 * <p>Both conditions, because the menu is an offer. Advertising a workspace a tenant has not been
 * rolled onto is as wrong as advertising one the user has no right to — they just fail differently.
 */
export function isSectionVisible(
  section: AppSection,
  context: SectionPermissionContext,
  navVisibility?: NavVisibility,
): boolean {
  if (section.featureKey && navVisibility && !navVisibility[section.featureKey]) {
    return false;
  }
  return isSectionPermitted(section, context);
}

/** Every section the sidebar should list, in order. */
export function availableSections(
  context: SectionPermissionContext,
  navVisibility?: NavVisibility,
): AppSection[] {
  return APP_SECTIONS.filter((section) => isSectionVisible(section, context, navVisibility));
}

/**
 * Where to send someone who cannot stay where they are.
 *
 * <p>Prefers a section the sidebar actually lists, so the destination is one they can find their way
 * back to. Falls back to anything their rights open — including a staged-off or `navHidden` section —
 * rather than giving up, because the alternative is telling someone who *does* hold a grant that they
 * have no access at all. A loyalty-only employee is the real case: `/loyalty/customers` is a customer
 * search they cannot run, so their only destination is the programme screen, which has no sidebar
 * entry of its own.
 *
 * <p>The dashboard is always skipped: the caller is redirecting *away* from it. `undefined` means the
 * account can genuinely reach nothing, which the caller must state plainly rather than redirect into
 * a loop.
 */
export function firstAvailableSection(
  context: SectionPermissionContext,
  navVisibility?: NavVisibility,
): AppSection | undefined {
  const listed = availableSections(context, navVisibility).filter(
    (section) => section.key !== 'dashboard' && !section.navHidden,
  );
  if (listed.length > 0) {
    return listed[0];
  }
  return APP_SECTIONS.filter((section) => section.key !== 'dashboard').find((section) =>
    isSectionPermitted(section, context),
  );
}
